import { useState } from 'react'
import Modal from '@components/common/Modal'
import useOwnDeliveryAddress from '@components/reports/queue/useOwnDeliveryAddress'
import reportPackService from '@api/v1/reportPackService'
import PackCadenceFields from './PackCadenceFields'
import { PACK_FORMAT_LABELS, PACK_MAX_MEMBERS, PACK_MIN_MEMBERS, localTimeZone } from './packFormat'

/**
 * Create or edit a report pack (spec 172).
 *
 * Members are chosen from the caller's OWN standard-report schedules and sent as schedule
 * ids; cb-api copies each one's report and filters server-side. So a pack runs exactly what
 * its owner already saved, with each report's own date window — this dialog never touches a
 * filter. The order chosen here is the order of the workbook's sheets.
 */

const defaultCadence = () => ({ freq: 'weekly', interval: 1, byDay: [1], byMonthDay: 1, hour: 7, minute: 0, tz: localTimeZone() })

const PackEditorModal = ({ pack = null, schedules = [], onClose, onSaved }) => {
  const editing = !!pack
  const [name, setName] = useState(pack?.name || '')
  const [selected, setSelected] = useState(() => (pack?.members || [])
    .slice().sort((a, b) => a.position - b.position)
    .map((m) => m.sourceScheduleId).filter(Boolean))
  const [cadence, setCadence] = useState(() => (pack?.recurrence ? { ...defaultCadence(), ...pack.recurrence } : defaultCadence()))
  const [format, setFormat] = useState(pack?.deliverFormat || 'workbook')
  const [keep, setKeep] = useState(pack?.retainCount || 5)
  const [ownerAddress, setOwnerAddress] = useState('')
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const own = useOwnDeliveryAddress()
  // An account whose sign-in carries no email must supply one: a pack always emails its owner.
  const needsAddress = own.status === 'ready' && !own.address && !(editing && pack?.hasOwnerAddress)

  const byId = new Map(schedules.map((s) => [String(s.id), s]))
  const labelOf = (id) => {
    const s = byId.get(String(id))
    return (s?.name || '').trim() || (s ? `Report ${s.reportId}` : 'A schedule that no longer exists')
  }

  const toggle = (id) => {
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= PACK_MAX_MEMBERS ? cur : [...cur, id]))
  }
  const move = (i, delta) => {
    setSelected((cur) => {
      const j = i + delta
      if (j < 0 || j >= cur.length) return cur
      const next = cur.slice()
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
  }

  const save = async () => {
    setError(null)
    if (!name.trim()) { setError('Give the pack a name.'); return }
    if (selected.length < PACK_MIN_MEMBERS || selected.length > PACK_MAX_MEMBERS) {
      setError(`Choose between ${PACK_MIN_MEMBERS} and ${PACK_MAX_MEMBERS} of your scheduled reports.`)
      return
    }
    if (selected.some((id) => !byId.has(String(id)))) {
      setError('One of the chosen schedules no longer exists. Remove it and choose again.')
      return
    }
    if (needsAddress && !ownerAddress.trim()) { setError('Enter the email address the pack should be sent to.'); return }
    const payload = {
      name: name.trim(),
      scheduleIds: selected,
      recurrence: cadence,
      deliverFormat: format,
      retainCount: Number(keep),
      ...(ownerAddress.trim() ? { deliverOwnerAddress: ownerAddress.trim() } : {}),
    }
    setSaving(true)
    try {
      const saved = editing ? await reportPackService.update(pack.id, payload) : await reportPackService.create(payload)
      onSaved?.(saved)
    } catch (e) {
      setError(e?.message || "The report pack couldn't be saved.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={editing ? 'Edit report pack' : 'New report pack'}
      subtitle="Several of your scheduled reports, run together and sent as one email."
      onClose={onClose}
      maxWidth="max-w-2xl"
      labelledBy="pack-editor-title"
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-cb-toolbar">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="btn-cb-primary">
            {saving ? 'Saving…' : editing ? 'Save pack' : 'Create pack'}
          </button>
        </div>
      )}
    >
      <div className="space-y-5">
        <div>
          <label htmlFor="pack-name" className="mb-1 block text-[11px] font-semibold text-rc-ink">Name</label>
          <input
            id="pack-name"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Managing partner reports"
            className="input-rc w-full"
          />
        </div>

        <fieldset>
          <legend className="mb-1 text-[11px] font-semibold text-rc-ink">
            Reports ({selected.length} of {PACK_MAX_MEMBERS} chosen)
          </legend>
          <p className="mb-2 text-[11.5px] text-gray-600">
            Choose from your scheduled standard reports. Each keeps the filters and date range of its schedule.
            Pause a schedule if you only want it in the pack.
          </p>
          {schedules.length === 0 ? (
            <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[12px] text-gray-700" data-testid="pack-no-schedules">
              You have no scheduled standard reports yet. Schedule each report you want in the pack first, from the report&apos;s own page.
            </p>
          ) : (
            <ul className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-2">
              {schedules.map((s) => {
                const id = String(s.id)
                const checked = selected.includes(id)
                const full = !checked && selected.length >= PACK_MAX_MEMBERS
                return (
                  <li key={id}>
                    <label className="flex items-center gap-2 text-[12.5px] text-gray-800">
                      <input type="checkbox" checked={checked} disabled={full} onChange={() => toggle(id)} />
                      <span>{labelOf(id)}</span>
                      {!s.enabled && <span className="text-[11px] text-gray-600">(paused)</span>}
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
          {selected.length > 0 && (
            <div className="mt-2">
              <p id="pack-order-label" className="mb-1 text-[11.5px] text-gray-600">Order in the email and the workbook</p>
              <ol aria-labelledby="pack-order-label" className="space-y-1">
                {selected.map((id, i) => (
                  <li key={id} className="flex items-center gap-2 text-[12.5px] text-gray-800">
                    <span className="w-5 text-right text-gray-600">{i + 1}.</span>
                    <span className="flex-1">{labelOf(id)}</span>
                    <button type="button" className="btn-cb-toolbar px-2! py-0.5!" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${labelOf(id)} up`}>↑</button>
                    <button type="button" className="btn-cb-toolbar px-2! py-0.5!" onClick={() => move(i, 1)} disabled={i === selected.length - 1} aria-label={`Move ${labelOf(id)} down`}>↓</button>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </fieldset>

        <PackCadenceFields value={cadence} onChange={setCadence} />

        <fieldset>
          <legend className="mb-1 text-[11px] font-semibold text-rc-ink">Send me</legend>
          {Object.entries(PACK_FORMAT_LABELS).map(([value, label]) => (
            <label key={value} className="flex items-center gap-2 text-[12.5px] text-gray-800">
              <input type="radio" name="pack-format" value={value} checked={format === value} onChange={() => setFormat(value)} />
              {label}
            </label>
          ))}
          <p className="mt-1 text-[11.5px] text-gray-600">
            The workbook holds Centerbase&apos;s own Excel export of each report, with its filters, plus a Contents sheet.
            A report that fails or doesn&apos;t finish is named in the email with the reason.
          </p>
        </fieldset>

        {needsAddress && (
          <div>
            <label htmlFor="pack-owner-address" className="mb-1 block text-[11px] font-semibold text-rc-ink">Your email address</label>
            <input
              id="pack-owner-address"
              type="email"
              value={ownerAddress}
              onChange={(e) => setOwnerAddress(e.target.value)}
              className="input-rc w-full"
            />
            <p className="mt-1 text-[11.5px] text-gray-600">Your sign-in has no email address, so the pack needs one to be sent to.</p>
          </div>
        )}

        <div>
          <label htmlFor="pack-keep" className="mb-1 block text-[11px] font-semibold text-rc-ink">Keep results</label>
          <select id="pack-keep" value={String(keep)} onChange={(e) => setKeep(Number(e.target.value))} className="select-rc">
            {[3, 5, 12, 24].map((n) => <option key={n} value={n}>Last {n} runs</option>)}
          </select>
        </div>

        {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-[12.5px] text-red-800">{error}</p>}
      </div>
    </Modal>
  )
}

export default PackEditorModal
